import { parse } from 'tldts';
import { z } from 'zod';

export const ZodUrl = z.string().url().brand('Url');

export const ZodUrlOrUrlWithoutProtocol = ZodUrl.or(
  z
    .string()
    .refine((s) => {
      const result = parse(`https://${s}`);
      return Boolean(result.domain && result.isIcann);
    })
    .transform((s) => `https://${s}`)
    .brand('Url'),
);

export type Url = z.infer<typeof ZodUrl>;

declare const UrlPath: unique symbol;
export type UrlPath = string & { [UrlPath]: 'url-path' };

const ZodUrlPath = z
  .string()
  .refine((s) => s.startsWith('/'))
  .transform((s) => s as UrlPath);

export const isUrl = (maybeUrl: string): maybeUrl is Url => {
  const { success } = ZodUrl.safeParse(maybeUrl);
  return success;
};

export const isUrlPath = (maybeUrlPath: string): maybeUrlPath is UrlPath => {
  const { success } = ZodUrlPath.safeParse(maybeUrlPath);
  return success;
};

export const toUrlPath = (urlPathString: string) => {
  if (isUrlPath(urlPathString)) {
    return urlPathString;
  }
  throw new Error(`Invalid URL path ${urlPathString}`);
};

export const toUrl = (url: string) => {
  if (isUrl(url)) {
    return url;
  }
  throw new Error(`Invalid URL ${url}`);
};

export const appendUrl = (url: Url, urlPath: UrlPath): Url => `${url}${urlPath}` as Url;

export const constructUrl = (base: string | Url, ...paths: Array<string | UrlPath>) => {
  if (isUrl(base)) {
    let url = base;
    for (const path of paths) {
      if (isUrlPath(path)) {
        url = appendUrl(url, path);
      }
    }
    return url;
  }
  throw new Error('Not a url');
};
